import {useCallback, useEffect, useRef, useState} from 'react';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers';

import {
    useUpdateBusinessPartnerOnboarding,
    useUploadBusinessPartnerDocument,
} from 'api/business-partner';
import Form, {FormOnSubmit, WithFormError} from 'components/ui/Form/Form/Form';
import {
    OwnershipStructureFormValues,
    ownershipStructureSchema,
} from 'form/validation/businessPartner';
import {translate} from 'helpers/localize';
import NextStepButton from '../components/NextStepButton';
import StepTitle from '../components/StepTitle';
import {IDefaultStepProps} from '../useOnboarding';
import styles from './Steps.module.scss';
import CheckBoxGroup from 'components/ui/Form/OnboardingCheckBoxGroup/OnboardingCheckBoxGroup';
import FileUploadArea from 'components/ui/Form/FileUploadArea/FileUploadArea';
import useFileUploadStatuses, {
    isNewFileUploadStatus,
} from 'components/ui/Form/FileUploadButton/useFileUploadStatuses';
import {findOnboardingStepByNumber} from '../onboardingSteps';
import {handleTypedFormError} from 'helpers/handleFormError';
import {ComplexStructureDetails} from 'types/api';

export const convertToTranslatedOptions = (options: {}) =>
    Object.keys(options).map(key => ({
        '@id': key.split('-')[0],
        label: translate(options[key as keyof typeof options]),
    }));

const DEFAULT_FORM_VALUES: OwnershipStructureFormValues = {
    gwgInfo: {
        isGroup: '',
        threeLevelsOwnership: null,
        trustsOrNomineeShareholders: null,
        offshoreEntities: null,
    },
    companyOrgChartFiles: [],
};

const OwnershipStructure = (props: IDefaultStepProps) => {
    const {
        activeBusinessPartner,
        setActiveBusinessPartner,
        onSuccess,
        inReview,
        currentStep,
        bpDocuments,
    } = props;
    const [defaultValues, setDefaultValues] =
        useState<Partial<OwnershipStructureFormValues>>(DEFAULT_FORM_VALUES);
    const uploading = useRef<boolean>(false);

    const formMethods = useForm<WithFormError<OwnershipStructureFormValues>>({
        resolver: yupResolver(ownershipStructureSchema),
        defaultValues,
    });
    const {updateStatus, uploadStatuses} = useFileUploadStatuses();
    const {mutate: uploadBpDocument} = useUploadBusinessPartnerDocument();
    const {watch} = formMethods;

    const {mutate: addAdditionalInfo, isPending: isLoading} =
        useUpdateBusinessPartnerOnboarding();

    const isGroupValue = watch('gwgInfo.isGroup');
    const companyOrgChartDocuments = watch('companyOrgChartFiles');

    const onSubmit: FormOnSubmit<OwnershipStructureFormValues> = (
        values,
        setError,
    ) => {
        const {
            // TODO - fix after yup upgrade
            // Typescript can't ignore a block, it's either one line or an entire file...
            gwgInfo: {
                //@ts-ignore
                isGroup,
                //@ts-ignore
                threeLevelsOwnership,
                //@ts-ignore
                trustsOrNomineeShareholders,
                //@ts-ignore
                offshoreEntities,
            },
        } = values;
        const complexStructureDetails: ComplexStructureDetails[] = [];

        if (threeLevelsOwnership === '1') {
            complexStructureDetails.push('3_level');
        }
        if (trustsOrNomineeShareholders === '1') {
            complexStructureDetails.push('trust_shareholder');
        }
        if (offshoreEntities === '1') {
            complexStructureDetails.push('offshore');
        }
        const data = {
            gwgInfo: {
                complexStructure: complexStructureDetails.length > 0,
                complexStructureDetails,
                isGroup: isGroup ? +isGroup === 1 : false,
            },
            businessPartnerId: activeBusinessPartner['@id'],
            step: findOnboardingStepByNumber(currentStep)?.type,
        };

        addAdditionalInfo(data, {
            onSuccess: response => {
                setActiveBusinessPartner(response);
                onSuccess({refetchCrpList: true});
            },
            onError: (requestErrors: any) => {
                handleTypedFormError(requestErrors, setError, values);
            },
        });
    };

    useEffect(() => {
        const {gwgInfo} = activeBusinessPartner;
        const complexStructureDetails = gwgInfo?.complexStructureDetails;
        setDefaultValues({
            gwgInfo: {
                isGroup: gwgInfo?.isGroup ? '1' : '0',
                threeLevelsOwnership: complexStructureDetails
                    ? complexStructureDetails.includes('3_level')
                        ? '1'
                        : '0'
                    : null,
                trustsOrNomineeShareholders: complexStructureDetails
                    ? complexStructureDetails.includes('trust_shareholder')
                        ? '1'
                        : '0'
                    : null,
                offshoreEntities: complexStructureDetails
                    ? complexStructureDetails.includes('offshore')
                        ? '1'
                        : '0'
                    : null,
            },
            companyOrgChartFiles: [],
        });
    }, [activeBusinessPartner, inReview]);

    const handleUploadFile = useCallback(
        (files: File[], category: string) => {
            if (uploading.current === true) return;
            uploading.current = true;
            const needToUpload = files.filter(
                doc =>
                    uploadStatuses.find(
                        status =>
                            isNewFileUploadStatus(status) &&
                            status.document === doc,
                    ) === undefined,
            );
            if (!needToUpload.length) return;
            updateStatus({
                document: needToUpload[0],
                status: 'loading',
                name: needToUpload[0].name,
            });
            uploadBpDocument(
                {
                    activeBpId: activeBusinessPartner['@id'],
                    files,
                    type: 'onboarding',
                    category,
                },
                {
                    onSuccess: data => {
                        formMethods.setValue(
                            'file',
                            files.filter(d => d !== files[0]),
                        );
                        updateStatus({
                            document: needToUpload[0],
                            name: needToUpload[0].name,
                            status: 'uploaded',
                        });
                    },
                    onError: (errorResponse: any) => {
                        updateStatus({
                            document: needToUpload[0],
                            status: 'error',
                            name: needToUpload[0].name,
                            errorMessage:
                                errorResponse['hydra:description'] ||
                                'Something went wrong',
                        });
                    },
                    onSettled: () => {
                        uploading.current = false;
                    },
                },
            );
        },
        [
            activeBusinessPartner,
            formMethods,
            updateStatus,
            uploadBpDocument,
            uploadStatuses,
        ],
    );

    useEffect(() => {
        if (companyOrgChartDocuments?.length) {
            handleUploadFile(companyOrgChartDocuments, 'company_org_chart');
        }
    }, [companyOrgChartDocuments, handleUploadFile]);

    return (
        <>
            <StepTitle title={'Ownership structure'} />
            <Form<OwnershipStructureFormValues>
                onSubmit={onSubmit}
                formMethods={formMethods}
                defaultValues={defaultValues}
                className={styles.form}
                confirmLeave
            >
                <CheckBoxGroup
                    name="gwgInfo.isGroup"
                    options={[
                        {label: 'No', value: '0'},
                        {label: 'Yes', value: '1'},
                    ]}
                    label="Does your company have parent companies or subsidiaries? Is your company part of a group structure?"
                    type="radio"
                    variant="dark"
                    size="large"
                />
                {isGroupValue && +isGroupValue === 1 ? (
                    <>
                        <FileUploadArea
                            uploadStatuses={uploadStatuses}
                            name="companyOrgChartFiles"
                            label="Please upload your group structure as file (.pdf/.png/.jpg)"
                            info="The group structure overview should contain ownership ratios in % and ratios up to the effective beneficial owner (private person)."
                            uploadedDocuments={
                                bpDocuments
                                    ?.filter(
                                        doc =>
                                            doc.category ===
                                            'company_org_chart',
                                    )
                                    .map(doc => ({
                                        id: doc['@id'],
                                        name: doc.originalFileName,
                                    })) || []
                            }
                        />
                        <CheckBoxGroup
                            name="gwgInfo.threeLevelsOwnership"
                            options={[
                                {label: 'No', value: '0'},
                                {label: 'Yes', value: '1'},
                            ]}
                            label="Does your company have more than 3 levels of ownership?"
                            info="This would mean there are more than 3 layers of companies or entities between the owners and your company."
                            type="radio"
                            variant="dark"
                            size="large"
                        />
                        <CheckBoxGroup
                            name="gwgInfo.trustsOrNomineeShareholders"
                            options={[
                                {label: 'No', value: '0'},
                                {label: 'Yes', value: '1'},
                            ]}
                            label="Does your company ownership structure include trusts or nominee shareholders?"
                            info="These are indirect arrangements that may help manage ownership."
                            type="radio"
                            variant="dark"
                            size="large"
                        />
                        <CheckBoxGroup
                            name="gwgInfo.offshoreEntities"
                            options={[
                                {label: 'No', value: '0'},
                                {label: 'Yes', value: '1'},
                            ]}
                            label="Does your company ownership structure include any offshore entities?"
                            info="Offshore entities are companies set up in jurisdictions like the Cayman Islands, British Virgin Islands, Panama, etc."
                            type="radio"
                            variant="dark"
                            size="large"
                        />
                    </>
                ) : null}

                <NextStepButton loading={isLoading} />
            </Form>
        </>
    );
};

export default OwnershipStructure;
